import { useState } from 'react';
import { startOfMonth } from 'date-fns';
import numeral from 'numeral';

import { Helmet } from 'react-helmet';
import { PieChart, Pie, ResponsiveContainer, Tooltip, Legend, Cell } from 'recharts';
import DateRangePicker from "@wojtekmaj/react-daterange-picker";

import SectionHeader from '../SectionHeader';

import useDesignAndEngineeringProjectStatusChart from '../../hooks/useDesignAndEngineeringProjectStatusChart';
import useDesignAndEngineeringClientChart from '../../hooks/useDesignAndEngineeringClientChart';
import useDesignAndEngineeringServiceExpenseChart from '../../hooks/useDesignAndEngineeringExpenseChart';
import useDesignAndEngineeringTotalQuotedChart from '../../hooks/useDesignAndEngineeringTotalQuotedChart';

import { formatDateToSydneyTimeZone } from '../../utils/dateUtils';

const chartColors = ['#298399', '#7dd3fc', '#0ea5e9', '#0369a1', '#60a5fa', '#3b82f6', '#1d4ed8', '#6ee7b7', '#10b981', '#047857'];
const statusChartColors = {
  Pending: '#facc15',
  'In progress': '#298399',
  Completed: '#34d399',
  Lost: '#f87171',
  Invoiced: '#38bdf8'
}

const DesignAndEngineeringOverview = () => {
  const [dateRange, setDateRange] = useState([startOfMonth(new Date()), new Date()]);

  const { data: projectStatusChartData } = useDesignAndEngineeringProjectStatusChart({
    startDate: dateRange?.[0],
    endDate: dateRange?.[1]
  });

  const projectStatusChart = projectStatusChartData?.chartData;

  const { data: clientChartData } = useDesignAndEngineeringClientChart({
    startDate: dateRange?.[0],
    endDate: dateRange?.[1]
  });

  const projectClientChart = clientChartData?.chartData;

  const { data: totalQuotedChartData } = useDesignAndEngineeringTotalQuotedChart({
    startDate: dateRange?.[0],
    endDate: dateRange?.[1]
  });

  const totalQuotedChart = totalQuotedChartData?.chartData;

  return (
    <>
      <Helmet>
        <title>D & E Overview | ASE Group</title>
      </Helmet>

      <div className='bg-ase-blue px-4 py-2 rounded-md mb-4 font-semibold flex items-center justify-between'>
        <p className='text-white'>{formatDateToSydneyTimeZone(new Date(), 'dd/MM/yyyy HH:mm')}</p>

        <div className='flex flex-col md:flex-row items-center gap-4'>
          <p className='text-white text-xs hidden md:block'>Period:</p>

          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            format='dd/MM/y'
            className="filter-date-range"
          />
        </div>
      </div>


      <div className='border border-ase-blue rounded-lg'>
        <SectionHeader title='Project Overview' />

        <div className='grid grid-cols-3 gap-4 bg-white rounded-b-lg'>
          <div className='flex flex-col items-center justify-center py-4'>
            <p className='text-sm md:text-normal font-semibold text-center'>Total projects</p>
            <p className='text-[18px] md:text-[32px] text-center'>
              {projectStatusChart?.length > 0 && projectStatusChart.reduce((acc, curr) => acc + curr.count, 0)}
            </p>
          </div>

          <div className='flex flex-col items-center justify-center py-4'>
            <p className='text-sm md:text-normal font-semibold text-center'>Total quoted</p>
            <p className='text-[18px] md:text-[32px] text-center'>
              {totalQuotedChartData?.chartData && numeral(totalQuotedChartData.chartData.reduce((acc, curr) => acc + curr.count, 0)).format('$0,0.00')}
            </p>
          </div>

          <div className='flex flex-col items-center justify-center py-4'>
            <p className='text-sm md:text-normal font-semibold text-center'>Total margin</p>
            <p className='text-[18px] md:text-[32px] text-center'>
              {numeral(totalQuotedChartData?.totalMargin).format('$0,0.00')}
            </p>
          </div>
        </div>
      </div>

      <div className='border border-ase-blue rounded-lg mt-4'>
        <SectionHeader title='Chart overview' />

        <div className='grid md:grid-cols-3 gap-4 md:h-[350px] bg-white rounded-b-lg'>
          <div>
            <p className='font-semibold text-center py-4'>Quoted</p>

            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  dataKey="count"
                  isAnimationActive={true}
                  data={totalQuotedChart}
                  outerRadius={90}
                  fill="#19668B"
                  label={(data) => numeral(data.value).format('$0,0.00')}
                  fontSize={14}
                  legendType='circle'
                >
                  {totalQuotedChart?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={chartColors[index]} />
                  ))}
                </Pie>

                <Legend iconSize={5} />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div>
            <p className='font-semibold text-center py-4'>By status</p>

            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  dataKey="count"
                  isAnimationActive={true}
                  data={projectStatusChart}
                  outerRadius={90}
                  fill="#19668B"
                  label
                  fontSize={14}
                  legendType='circle'
                >
                  {projectStatusChart?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={statusChartColors[entry.name]} />
                  ))}
                </Pie>

                <Tooltip />
                <Legend iconSize={5} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div>
            <p className='font-semibold text-center py-4'>By client</p>

            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  dataKey="count"
                  isAnimationActive={true}
                  data={projectClientChart}
                  outerRadius={90}
                  fill="#19668B"
                  fontSize={14}
                  legendType='circle'
                >
                  {projectClientChart?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={chartColors[index]} />
                  ))}
                </Pie>

                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  )
}

export default DesignAndEngineeringOverview;
